import bookaMeetingApi from './request';


export type InstagramAccount = {
  userId: string;
  igId: string;
  name: string;
  username: string;
  savedAt: string;
  updatedAt: string;
};

type InstagramAccountsResponse = {
  success?: boolean;
  accounts?: InstagramAccount[];
  account?: InstagramAccount;
  error?: string;
};

export async function updateUserInstagramAccount(data: {
  phoneNumberId: string;
  userId: string;
  username: string;
  name: string;
}): Promise<{ success: boolean; message?: string; error?: string }> {
  const response = await bookaMeetingApi.requestWIthAuth<{
    success: boolean;
    message?: string;
    error?: string;
  }>({
    url: '/user/instagram-account',
    method: 'PATCH',
    data,
  });
  return response.data;
}

export type InstagramMessageUser = {
  customerId: string;
  customerName: string;
  name?: string;
  username: string;
  channel?: string;
  businessIgId: string;
  lastMessage: string;
  timestamp: string;
  updatedAt: string;
};

export type InstagramThreadMessage = {
  mid: string;
  customerId: string;
  businessIgId: string;
  customerName: string;
  username: string;
  direction: string;
  source: string;
  type: string;
  text: string;
  timestamp: string;
  updatedAt?: string;
};

export async function getInstagramMessageUsers(businessIgId?: string): Promise<InstagramMessageUser[]> {
  const response = await bookaMeetingApi.requestWIthAuth<{
    success?: boolean;
    users?: InstagramMessageUser[];
  }>({
    url: '/instagram/message-users',
    method: 'GET',
    params: businessIgId ? { businessIgId } : undefined,
  });
  return response.data?.users || [];
}

export async function getInstagramUserMessages(
  customerId: string,
  businessIgId?: string
): Promise<InstagramThreadMessage[]> {
  const response = await bookaMeetingApi.requestWIthAuth<{
    success?: boolean;
    messages?: InstagramThreadMessage[];
  }>({
    url: '/instagram/user-messages',
    method: 'GET',
    params: {
      customerId,
      ...(businessIgId ? { businessIgId } : {}),
    },
  });
  return response.data?.messages || [];
}

export async function sendInstagramHumanReply(data: {
  recipientId: string;
  text: string;
  businessIgId?: string;
  humanAgent?: boolean;
}): Promise<{ success?: boolean; messageId?: string; error?: string }> {
  const response = await bookaMeetingApi.requestWIthAuth<{
    success?: boolean;
    messageId?: string;
    error?: string;
  }>({
    url: '/instagram/human-agent',
    method: 'POST',
    data,
  });
  return response.data;
}

export type InstagramComment = {
  mid: string;
  commentId: string;
  customerId: string;
  customerName: string;
  username: string;
  businessIgId: string;
  direction: string;
  source: string;
  text: string;
  timestamp: string;
  isReply: boolean;
};

export async function getInstagramComments(businessIgId?: string): Promise<InstagramComment[]> {
  const response = await bookaMeetingApi.requestWIthAuth<{
    success?: boolean;
    comments?: InstagramComment[];
  }>({
    url: '/instagram/comments',
    method: 'GET',
    params: businessIgId ? { businessIgId } : undefined,
  });
  return response.data?.comments || [];
}

export async function replyToInstagramComment(data: {
  commentId: string;
  message: string;
  businessIgId?: string;
  customerId?: string;
}): Promise<{ success?: boolean; id?: string; error?: string }> {
  const response = await bookaMeetingApi.requestWIthAuth<{
    success?: boolean;
    id?: string;
    error?: string;
  }>({
    url: '/instagram/comment-reply',
    method: 'POST',
    data,
  });
  return response.data;
}

export async function publishInstagramPost(data: {
  imageUrl: string;
  caption?: string;
  businessIgId?: string;
}): Promise<{ success?: boolean; mediaId?: string; error?: string }> {
  const response = await bookaMeetingApi.requestWIthAuth<{
    success?: boolean;
    mediaId?: string;
    error?: string;
  }>({
    url: '/instagram/publish',
    method: 'POST',
    data,
  });
  return response.data;
}

export type InstagramInsight = {
  name?: string;
  title?: string;
  period?: string;
  id?: string;
  description?: string;
  values?: { value?: number | string | Record<string, unknown>; end_time?: string }[];
};

export type InstagramProfileStats = {
  username?: string;
  name?: string;
  followersCount?: number | null;
  followsCount?: number | null;
  mediaCount?: number | null;
};

export async function getInstagramInsights(params: {
  businessIgId?: string;
  mediaId?: string;
}): Promise<{ success: boolean; rows: InstagramInsight[]; profile: InstagramProfileStats | null; reachLast28Days: number | null; error?: string }> {
  const response = await bookaMeetingApi.requestWIthAuth<{
    success?: boolean;
    profile?: InstagramProfileStats | null;
    reachLast28Days?: number | null;
    data?: InstagramInsight[] | { data?: InstagramInsight[] };
    error?: string;
  }>({
    url: '/instagram/insights',
    method: 'GET',
    params: {
      ...(params.businessIgId ? { businessIgId: params.businessIgId } : {}),
      ...(params.mediaId ? { mediaId: params.mediaId } : {}),
    },
  });
  const payload = response.data;
  const raw = payload?.data;
  const rows = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
  return {
    success: Boolean(payload?.success),
    rows,
    profile: payload?.profile || null,
    reachLast28Days: typeof payload?.reachLast28Days === 'number' ? payload.reachLast28Days : null,
    error: payload?.error,
  };
}

export async function getInstagramAccounts(): Promise<InstagramAccount[]> {
  const response = await bookaMeetingApi.requestWIthAuth<InstagramAccountsResponse>({
    url: '/instagram/account',
    method: 'GET',
  });
  const data = response.data;
  if (Array.isArray(data?.accounts)) return data.accounts;
  if (data?.account) return [data.account];
  return [];
}

